import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultPromocode, defaultPromocodeFilter, defaultPromocodesIsValid, defaultPromocodeValidation } from '../constants/promocodes';
import {
  IEditPromocode,
  IPromocodesFilter,
  IPromocodesIsValid,
  PROMOCODE_DISCOUNT_TYPE,
  PROMOCODE_MENU,
  PROMOCODE_PROVIDE_AS,
  TPromocode,
  IPromocodeValidationErrors,
} from '../types/promocodes';
import { defaultPagination, LOCALES } from 'src/constants/main';
import client from 'src/services/client';
import { IPagination } from 'src/types/main';
import { getCancelToken } from 'src/utils/helpers';
import { dateFormatForRequest } from '~/constants/dateTime';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';
import { modalStore } from '~/mobx/modalStore';
import { getInputFieldErrorText } from '~/helpers/getInputFieldErrorText/getInputFieldErrorText';

export class PromocodeStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  cancel: Canceler | null = null;

  @observable pagination: IPagination<TPromocode> = defaultPagination;
  @observable filters: IPromocodesFilter = defaultPromocodeFilter;
  @observable promocode: IEditPromocode = defaultPromocode;
  // editablePromocode: IEditPromocode = defaultPromocode;
  @observable isValid: IPromocodesIsValid = defaultPromocodesIsValid;
  @observable isEdit: boolean = false;
  @observable loading: boolean = false;
  @observable cashbackValidationErrorText = '';
  @observable descriptionValidationErrors: IPromocodeValidationErrors = defaultPromocodeValidation;

  public get isZeroDiscount(): boolean {
    return !Number(this.promocode.amount);
  }

  public get isPercentageDiscountType(): boolean {
    return this.promocode.discount_type === PROMOCODE_DISCOUNT_TYPE.PERCENTAGE;
  }

  public get isProvideAsDiscount(): boolean {
    return this.promocode.provide_as === PROMOCODE_PROVIDE_AS.DISCOUNT;
  }

  @action fetchPromocodes = async (page: number = this.pagination.current_page + 1) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      if (typeof page !== 'number') page = this.pagination.current_page + 1;

      this.pagination.loading = true;
      this.pagination.refreshing = true;

      const pagination = await client.get('/v3/promocodes', {
        params: {
          page,
          name: this.filters.name,
          sort_field: this.filters.sort_field,
          sort_dir: this.filters.sort_dir,
        },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });
      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
        data: pagination.data,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'PromocodeStore fetchPromocodes' });
    }
  };

  @action getPromocode = async (id: TPromocode['id']) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      const promocode: TPromocode = await client.get(`/v3/promocodes/${id}`);
      this.promocode = this.setPromocode(promocode);
      // this.editablePromocode = this.setPromocode(promocode);
      this.validator();

      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'PromocodeStore getPromocode', showModal: true });
    }
  };

  private setPromocode = (promocode: TPromocode): IEditPromocode => {
    return {
      promocode: promocode.promocode,
      amount: promocode.amount.toString(),
      start_date: moment(promocode.start_date).toDate(),
      end_date: moment(promocode.end_date).toDate(),
      discount_type: promocode.discount_type,
      provide_as: promocode.provide_as,
      is_all_day: promocode.is_all_day,
      delivery_types: promocode.delivery_types,
      products: promocode.products || undefined,
      categories: promocode.categories || undefined,
      user_limited: promocode.user_limited,
      usage_limited: promocode.usage_limited,
      is_free_delivery: promocode.is_free_delivery,
      first_order: promocode.first_order,
      applicability: promocode.applicability,
      minimum_order: promocode.minimum_order?.toString() || '0',
      max_discount_amount: promocode.max_discount_amount?.toString() || '',
      menu: promocode.menu,
      customers: promocode.customers || undefined,
      branches: promocode.branches || undefined,
      start_time: promocode.start_time ? moment(promocode.start_time, `hh:mm:ss`).toDate() : null,
      end_time: promocode.end_time ? moment(promocode.end_time, `hh:mm:ss`).toDate() : null,
      days_of_week: promocode.days_of_week,
      external_id: promocode.external_id || undefined,
      //
      allBranches: promocode.branches?.length > 0 ? false : true,
      allCustomers: promocode.customers?.length > 0 ? false : true,

      reoccurring_usage_limited: promocode.reoccurring_usage_limited ?? 0,
      reoccurring_usage_interval_type: promocode.reoccurring_usage_interval_type ?? 0,
      usage_number_per_interval: promocode.usage_number_per_interval ?? 0,
      usage_interval: promocode.usage_interval ?? 0,
      is_shown_as_offer: promocode?.is_shown_as_offer ?? false,
      description: promocode.description,
    };
  };

  @action editPromocode = async (id: TPromocode['id']) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      await client.put(`/v3/promocodes/${id}`, this.getParams());
      // const promocode: TPromocode = await client.put(`/v3/promocodes/${id}`, this.promocode);

      this.loading = false;
      // this.editablePromocode = this.setPromocode(promocode);
      this.isEdit = false;
      this.fetchPromocodes(1);

      toastSuccess({ message: localeStore.t('toast_promocode_changes_saved') });

      return true;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'PromocodeStore editPromocode', showModal: true });
    }
  };

  private getParams = () => {
    let data = {
      promocode: this.promocode.promocode,
      amount: Number(this.promocode.amount),
      start_date: moment(this.promocode.start_date).format(dateFormatForRequest),
      end_date: moment(this.promocode.end_date).format(dateFormatForRequest),
      discount_type: this.promocode.discount_type,
      provide_as: this.promocode.provide_as,
      is_all_day: this.promocode.is_all_day,
      delivery_types: this.promocode.delivery_types,
      products_id:
        this.promocode.menu === PROMOCODE_MENU.INCLUDE
          ? this.promocode.products?.filter((item) => !item.is_delete).map((product) => product.id)
          : undefined,
      categories_id: this.promocode.menu === PROMOCODE_MENU.ALL ? undefined : this.promocode.categories?.map((category) => category.id),
      user_limited: this.promocode.user_limited,
      usage_limited: this.promocode.usage_limited,
      is_free_delivery: this.promocode.is_free_delivery,
      first_order: this.promocode.first_order,
      applicability: this.promocode.applicability,
      minimum_order: Number(this.promocode.minimum_order),
      max_discount_amount: this.getMaxDiscountAmount(),
      menu: this.promocode.menu,
      reoccurring_usage_limited: this.promocode.reoccurring_usage_limited,
      reoccurring_usage_interval_type: this.promocode.reoccurring_usage_interval_type,
      customers_id: this.promocode.allCustomers ? [] : this.promocode.customers?.map((customer) => customer.id),
      branches_id: this.promocode.allBranches ? [] : this.promocode.branches?.map((branch) => branch.id),
      start_time: this.promocode.is_all_day ? null : moment(this.promocode.start_time).format('HH:mm'),
      end_time: this.promocode.is_all_day ? null : moment(this.promocode.end_time).format('HH:mm'),
      days_of_week: this.promocode.days_of_week,
      external_id: this.promocode.external_id,
      is_shown_as_offer: this.promocode.is_shown_as_offer,
      description: this.promocode.description,
    };

    if (this.promocode.reoccurring_usage_limited) {
      data = {
        ...data,
        ...{
          reoccurring_usage_interval_type: this.promocode.reoccurring_usage_interval_type ?? 0,
          usage_interval: this.promocode.usage_interval ?? 0,
          usage_number_per_interval: this.promocode.usage_number_per_interval ?? 0,
          reoccurring_usage_limited: this.promocode.reoccurring_usage_limited ?? 0,
        },
      };
    }

    return data;
  };

  private getMaxDiscountAmount = () => {
    if (this.promocode.discount_type === PROMOCODE_DISCOUNT_TYPE.AMOUNT || this.promocode.max_discount_amount?.length === 0) {
      return undefined;
    } else return Number(this.promocode.max_discount_amount);
  };

  @action deletePromocode = async (id: number) => {
    try {
      this.loading = true;

      await client.delete(`/v3/promocodes/${id}`);

      this.loading = false;
      this.isEdit = false;

      this.fetchPromocodes(1);

      toastSuccess({ message: localeStore.t('toast_promocode_deleted') });

      return true;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'PromocodeStore deletePromocode', showModal: true });
    }
  };

  @action createPromocode = async () => {
    try {
      this.loading = true;

      await client.post('/v3/promocodes', this.getParams());

      this.loading = false;
      this.isEdit = false;
      this.fetchPromocodes(1);

      toastSuccess({ message: localeStore.t('toast_promocode_changes_saved') });

      return true;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'PromocodeStore createPromocode', showModal: true });
    }
  };

  @action setFilters = (filters: IPromocodesFilter, clear: boolean) => {
    if (clear) {
      this.filters = filters;
    } else {
      this.filters = { ...this.filters, ...filters };
    }

    this.fetchPromocodes(1);
  };

  @action changePromocode = (promocode: IEditPromocode, validation?: boolean) => {
    this.promocode = { ...this.promocode, ...promocode };
    this.isEdit = true;
    if (validation) {
      this.validator();
    }
  };

  @action clearCashbackError = () => {
    this.cashbackValidationErrorText = '';
    this.isValid.amount = true;
  };

  @action validateCashback = () => {
    if (!this.isProvideAsDiscount) {
      if (!this.isPercentageDiscountType && Number(this.promocode.amount) <= 0) {
        this.isValid.amount = false;
        this.cashbackValidationErrorText = localeStore.t('cashback_amount_error');
        return false;
      } else if (this.isPercentageDiscountType && (Number(this.promocode.amount) <= 0 || Number(this.promocode.amount) > 100)) {
        this.cashbackValidationErrorText = localeStore.t('cashback_percent_count_error');
        this.isValid.amount = false;
        return false;
      } else {
        this.cashbackValidationErrorText = '';
        return true;
      }
    } else {
      return true;
    }
  };

  @action validateDescription = () => {
    this.descriptionValidationErrors.descriptionEn = getInputFieldErrorText(this.promocode.description?.en, LOCALES.EN, 100);
    this.descriptionValidationErrors.descriptionAr = getInputFieldErrorText(this.promocode.description?.ar, LOCALES.AR, 100);
    if (!!this.descriptionValidationErrors.descriptionEn || !!this.descriptionValidationErrors.descriptionAr) {
      return false;
    }
    return true;
  };

  @action validator = () => {
    // promocode validation promocode
    if (this.promocode.promocode) {
      this.isValid.promocode = true;
    } else this.isValid.promocode = false;
    // promocode validation amount
    if (this.promocode.amount && this.promocode.amount.length !== 0 && (this.promocode.amount === '0' || Number(this.promocode.amount))) {
      this.isValid.amount = true;
    } else this.isValid.amount = false;
    // promocode validation max_discount_amount
    if (this.isPercentageDiscountType && this.promocode.max_discount_amount !== '' && Number(this.promocode.max_discount_amount) === 0) {
      this.isValid.max_discount_amount = false;
    } else this.isValid.max_discount_amount = true;
    // promocode validation start_date
    if (this.promocode.start_date) {
      this.isValid.start_date = true;
    } else this.isValid.start_date = false;
    // promocode validation end_date
    if (this.promocode.end_date) {
      this.isValid.end_date = true;
    } else this.isValid.end_date = false;
    // promocode validation discount_type
    if (this.promocode.discount_type) {
      this.isValid.discount_type = true;
    } else this.isValid.discount_type = false;
    // promocode validation delivery_types
    if (this.promocode.delivery_types) {
      this.isValid.delivery_types = true;
    } else this.isValid.delivery_types = false;
    // promocode validation menu
    if (
      (this.promocode.menu === PROMOCODE_MENU.EXCLUDE && (this.promocode.categories?.length || 0) < 1) ||
      (this.promocode.menu === PROMOCODE_MENU.INCLUDE && (this.promocode.categories?.length || 0) < 1)
    ) {
      this.isValid.menu = false;
    } else {
      this.isValid.menu = true;
    }

    // promocode validation all
    if (
      this.isValid.promocode &&
      this.isValid.amount &&
      this.isValid.max_discount_amount &&
      this.isValid.start_date &&
      this.isValid.end_date &&
      this.isValid.discount_type &&
      this.isValid.delivery_types &&
      this.isValid.menu
    ) {
      this.isValid.all = true;
    } else this.isValid.all = false;
  };

  @action validateOffer = (): boolean => {
    if (this.promocode.is_shown_as_offer && this.promocode.promocode) {
      let message = '';

      // If promocode is personalized, i.e. designated for specific customers, user shall not be able to turn the toggle on.
      if (this.promocode.customers?.length && !this.promocode.allCustomers) {
        message = localeStore.t('promocode_show_as_public_offer_toggle_pers_err');
        // If promocode value is 0 AND it gives no free delivery at the same time, user shall not be able to turn the toggle on.
      }
      if (this.promocode.amount === '0' && !this.promocode.is_free_delivery) {
        message += `${message ? `\n` : ''}${localeStore.t('promocode_show_as_public_offer_toggle_nodicsount_err')}`;
      }

      if (message) {
        modalStore.openErrorModal({
          message,
          // After closing error, the public offer parameter toggle shall be turned off, user shall stay on create/edit page
          onClose: () => this.changePromocode({ is_shown_as_offer: false }, true),
          contentClassName: 'promocode_error',
        });
        return false;
      }
    }
    // promocode validation description
    if ((!!this.promocode.description?.en || !!this.promocode.description?.ar) && this.promocode.is_shown_as_offer) {
      return this.validateDescription();
    }

    return true;
  };

  @action unMount = () => {
    this.promocode = defaultPromocode;
    // this.editablePromocode = defaultPromocode;
    this.isEdit = false;
    this.isValid = defaultPromocodesIsValid;
    this.descriptionValidationErrors = defaultPromocodeValidation;
  };
}

export const promocodeStore = new PromocodeStore();
